import { Session } from "@models/ISchema";
import { Week } from "../context/linkData.reducer";
import { BlockRanges, EditedSession } from "../context/formData.reducer";
import { isOutsideThreshold } from "./isOutsideThreshold";

export type SessionLockState = "none" | "mixed" | "all";

const deriveBlocksFromSession = (session: Session | EditedSession): BlockRanges | null => {
  const editedBlocks = (session as EditedSession)?.blocks;
  if (editedBlocks?.selected) return editedBlocks;

  const blockRange = (session as Session)?.assignment?.blockRange;
  const day = (session as Session)?.assignment?.day;
  if (!blockRange || !day) return null;

  return {
    selected: "hours",
    day,
    startTime: blockRange?.start?.startingTime,
    endTime: blockRange?.end?.endingTime,
  };
};

const collectIntervals = (session: Session | EditedSession): Week[] => {
  // `sessionAdapter` turns every `Session` into an `EditedSession` whose
  // `intervals` hold the whole term calendar, flagging the assigned weeks with
  // `checked`. Only those weeks are the session's own, so filtering here keeps
  // both shapes equivalent to `assignment.intervals`.
  const editedIntervals = (session as EditedSession)?.intervals;
  if (editedIntervals?.length) return editedIntervals.filter(interval => interval?.checked);

  const assignmentIntervals = (session as Session)?.assignment?.intervals;
  return (assignmentIntervals ?? []) as unknown as Week[];
};

export const classifySessionLockState = (
  session: Session | EditedSession,
  thresholdDate: string | null,
  canEditPastSessions: boolean,
): SessionLockState => {
  if (!thresholdDate || canEditPastSessions) return "none";

  const intervals = collectIntervals(session);
  if (!intervals.length) return "none";

  const blocks = deriveBlocksFromSession(session);
  const outsideCount = intervals.reduce(
    (acc, interval) => (isOutsideThreshold(thresholdDate, interval, blocks) ? acc + 1 : acc),
    0,
  );

  if (outsideCount === 0) return "none";
  if (outsideCount === intervals.length) return "all";
  return "mixed";
};
